import React, { useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { message, Form } from 'antd';
import { useLoginMutation } from '../../store/services/authService';
import { useNavigate, Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../../store/store';
import YadLogo from '../../assets/icons/YadLogo';
import TitleText from '../atoms/TitleText';
import InfoText from '../atoms/InfoText';
import { AuthWrapper } from './AuthWrapper';
import CustomCheckbox from '../atoms/CustomCheckbox';
import Button from '../atoms/Button';
import Input from '../atoms/Input';
import PasswordInput from '../atoms/PasswordInput';

const REMEMBER_CREDENTIALS_KEY = 'yad_supplier_admin_login_credentials';

const loginSchema = z.object({
  email: z
    .string()
    .min(1, 'This field cannot be empty')
    .email('Please enter a valid email address'),
  password: z.string().min(1, 'This field cannot be empty'),
  rememberMe: z.boolean().optional(),
});

type LoginFormData = z.infer<typeof loginSchema>;

function getStoredCredentials(): { email: string; password: string } | null {
  try {
    const raw = localStorage.getItem(REMEMBER_CREDENTIALS_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { email?: string; password?: string };
    if (typeof parsed?.email === 'string' && typeof parsed?.password === 'string') {
      return { email: parsed.email, password: parsed.password };
    }
    return null;
  } catch {
    return null;
  }
}

function setStoredCredentials(email: string, password: string) {
  try {
    localStorage.setItem(
      REMEMBER_CREDENTIALS_KEY,
      JSON.stringify({ email, password })
    );
  } catch {
    // ignore storage errors
  }
}

function clearStoredCredentials() {
  try {
    localStorage.removeItem(REMEMBER_CREDENTIALS_KEY);
  } catch {
    // ignore
  }
}

export function Login() {
  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<LoginFormData>({
    defaultValues: {
      email: '',
      password: '',
      rememberMe: false,
    },
    resolver: zodResolver(loginSchema),
    mode: 'all',
  });

  const [login, { isLoading }] = useLoginMutation();
  const navigate = useNavigate();
  const isAuthenticated = useSelector(
    (state: RootState) => state.auth.isAuthenticated
  );

  // Pre-fill form with remembered credentials when the user returns to the platform
  useEffect(() => {
    const stored = getStoredCredentials();
    if (stored) {
      setValue('email', stored.email);
      setValue('password', stored.password);
      setValue('rememberMe', true);
    }
  }, [setValue]);

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  const onSubmit = async (data: LoginFormData) => {
    const newData = {
      username: data.email,
      password: data.password,
    };
    try {
      await login(newData).unwrap();
      if (data.rememberMe) {
        setStoredCredentials(data.email, data.password);
      } else {
        clearStoredCredentials();
      }
      message.success('Login successful!');
      navigate('/', { replace: true });
    } catch (error: any) {
      message.error(
        error?.data?.errors?.default ||
          Object.values(error?.data?.errors || {}).join(', ')
      );
    }
  };

  return (
    <AuthWrapper>
      <div className="w-full max-w-xl" data-test="login-page">
        <YadLogo width={120} height={120} />

        <div className="space-y-2 mt-4 mb-8">
          <TitleText
            text="Log in to YAD Portal"
            className="!text-2xl !font-bold"
          />
          <InfoText
            text="Welcome back! We're thrilled to see you again - let's make great progress together!"
            size="xs"
          />
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="mt-8" data-test="login-form">
          <Form.Item
            label={
              <span className="text-gray-900 font-medium text-sm">Email</span>
            }
            className="mb-4"
            layout="vertical"
          >
            <Controller
              name="email"
              control={control}
              render={({ field }) => (
                <Input
                  id="email"
                  value={field.value || ''}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  placeholder="email..."
                  autoComplete="email"
                  status={errors.email ? 'error' : undefined}
                  helperText={errors.email?.message}
                  className="rounded-lg border-gray-300"
                  data-test="login-email-input"
                />
              )}
            />
          </Form.Item>

          <Form.Item
            label={
              <span className="text-gray-900 font-medium text-sm">
                Password
              </span>
            }
            className="mb-4"
            layout="vertical"
          >
            <Controller
              name="password"
              control={control}
              render={({ field }) => (
                <PasswordInput
                  id="password"
                  value={field.value || ''}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  placeholder="***********"
                  autoComplete="current-password"
                  status={errors.password ? 'error' : undefined}
                  helperText={errors.password?.message}
                  className="rounded-lg border-gray-300"
                  data-test="login-password-input"
                />
              )}
            />
          </Form.Item>

          <div className="flex items-center justify-between mb-6">
            <Controller
              name="rememberMe"
              control={control}
              render={({ field }) => (
                <CustomCheckbox
                  value={field.value}
                  onChange={(e) => field.onChange(e.target.checked)}
                  className="text-gray-900"
                  data-test="login-remember-checkbox"
                >
                  Keep me logged in
                </CustomCheckbox>
              )}
            />
            <Link
              to="/forgot-password"
              className="text-gray-600 hover:text-gray-900 text-sm font-medium"
              data-test="login-forgot-password-link"
            >
              Forgot password?
            </Link>
          </div>

          <Form.Item className="">
            <Button
              type="submit"
              variant="dark"
              disabled={isLoading || isSubmitting}
              className="w-full font-semibold rounded-lg h-12"
              data-test="login-submit-btn"
            >
              {isLoading || isSubmitting ? 'Loading...' : 'Login'}
            </Button>
          </Form.Item>
        </form>
      </div>
    </AuthWrapper>
  );
}
